import { get } from 'lodash';
import React, { ReactNode } from 'react';

import Email from './Previews/Email';
import HtmlBody from './Previews/Emails/Html';
import Html2 from './Previews/Emails/Html2';
import PlainTextBody from './Previews/Emails/PlainText';
import { getProfileFirstName } from '~/BonjorosList/util';
import EditorContext, { EditorContextProps } from './Context';
import Replaceable from '~/components/higher-order/ReplaceableComponents';
import { EmailLayout } from '../../Types';
import Html3 from './Previews/Emails/Html3';
import Html4 from './Previews/Emails/Html4';

/**
 * TODO: https://bonjoro.atlassian.net/browse/PLG-78
 * Create email layout for Html3 and Html4 template
 */
export const previews: Record<EmailLayout, React.ReactNode> = {
  [EmailLayout.Html2]: Html2,
  [EmailLayout.Html]: HtmlBody,
  [EmailLayout.Plain]: PlainTextBody,
  [EmailLayout.Html3]: Html3,
  [EmailLayout.Html4]: Html4,
};

interface EmailTemplatePreviewProps {
  previews: Record<string, React.ElementType>;
}

@Replaceable(previews, 'previews')
export default class EmailTemplatePreview extends React.PureComponent<EmailTemplatePreviewProps> {
  static defaultProps: PreviewComponentProps = {
    image: 'https://loremflickr.com/50/50/face',
    playerImage: 'https://loremflickr.com/200/200/portrait',
  };

  render() {
    return (
      <EditorContext.Consumer>{this.renderPreview}</EditorContext.Consumer>
    );
  }

  renderPreview: (EditorContextProps) => ReactNode = ({
    user,
    metadata,
    compiledTemplates,
    ...context
  }: EditorContextProps) => {
    const { previews } = this.props;
    const preview = context.get<string>(
      ['email_template', 'layout'],
      'html'
    ) as string;

    const PreviewComponent = get(previews, preview);
    const logoId = context.get<string>(['landing_page', 'logo', 'id']);

    const props: PreviewComponentProps = {
      name: user?.name,
      bio: user?.bio ?? '',
      shortName: getProfileFirstName(user),
      subject: compiledTemplates?.subjectLine,
      message: compiledTemplates?.message,
      image: user?.image || EmailTemplatePreview.defaultProps.image,
      playerImage: get(
        metadata,
        'playerImage',
        EmailTemplatePreview.defaultProps.playerImage
      ),
      theme: {
        primaryColor: context.get<string>(
          ['theme', 'primary_color'],
          '#fc5a00'
        ) as string,
        secondaryColor: context.get<string>(
          ['theme', 'secondary_color'],
          '#ffffff'
        ) as string,
      },
      logoUrl: logoId && context.get<string>(['landing_page', 'logo', 'url']),
    };

    return (
      <Email
        name={props.name}
        image={props.image}
        subject={props.subject}
        body={this.renderEmailBody(PreviewComponent, props)}
      />
    );
  };

  renderEmailBody(
    PreviewComponent: React.ElementType,
    props: PreviewComponentProps
  ) {
    return <PreviewComponent {...props}>{props.message}</PreviewComponent>;
  }
}

export interface PreviewComponentProps {
  name?: string;
  shortName?: string;
  bio?: string;
  subject?: string;
  message?: ReactNode;
  image: string;
  playerImage: string;
  theme?: {
    primaryColor: string;
    secondaryColor: string;
  };
  logoUrl?: string;
  children?: ReactNode;
  hidePreviewTag?: boolean;
  bannerContent?: ReactNode;
  disclaimerContent?: ReactNode;
}
